/**
 * One chart's copy of a workflow, as one pure reducer: the version this
 * chart last confirmed and its content, what the person sees and edits,
 * the save in flight, versions that came early, and the person's undo and
 * redo. No IO: `useWorkflowDoc` in `./workflows.ts` feeds it the presence
 * room, save replies and the workflow read, and runs what it asks for.
 *
 * - Every new version enters one queue and applies strictly in version
 *   order: a `revision` from the room, a catch-up's revisions, and this
 *   chart's own save reply alike. One at or below the confirmed version is
 *   an acknowledgement and changes nothing. One that comes early waits; a
 *   gap still open after {@link GAP_WAIT_MS}, or a `sync` or a read naming
 *   a newer version, asks for the missing ones (`workflows.revisionsSince`).
 * - A version this chart saved (its `saveId` is one this chart sent) moves
 *   the confirmed version and leaves the screen alone.
 * - A whole version (a catch-up's head, a 409's latest, a read) that one of
 *   this chart's saves would have made is that save's, and is the same.
 * - Anyone else's version merges into what is on screen
 *   (`mergeWorkflowContent`): unsaved edits are kept and win any facet both
 *   changed. Their changes the merge left out, a step name neither side
 *   repeated, and a step the merge left with nothing leading to it are told
 *   in a notice. A 409 is the same merge with the reply's latest version,
 *   then the save goes again at once; a 409 with no newer version to merge
 *   reads the workflow and waits for it.
 * - Every version applied is kept in `applied`, as it changed the confirmed
 *   content, for undo.
 * - Undo takes back the person's own last edit with `revertChanges`, so a
 *   colleague's or eve's change made since stays. If one of them changed the
 *   same thing since, a change the merge kept off the screen included,
 *   nothing is undone and a notice names the step.
 * - Undo also takes back a whole agent request made for the person, on the
 *   server (`requests`): ⌘Z takes whichever is newer, the person's own last
 *   edit here or the request the server names, by version. The server answers
 *   with the version its Undo saved, which then arrives like anyone else's,
 *   cues included. A press while an Undo of a request or a read of the
 *   pointers is on its way waits for it, so two quick presses take back two
 *   things. After the server refuses one, the next press takes the person's
 *   own last edit first. Redo is the same, in reverse; the person's own edit
 *   on this chart since clears the server's Redo here, as it clears theirs.
 *
 * @module state/workflowDoc
 */
import {
  WORKFLOW_LIMITS,
  type WorkflowContentUpdate,
  type WorkflowGraph,
  type WorkflowRefusedStep,
  type WorkflowRequestPointers,
  type WorkflowRevision,
  type WorkflowRevisionActor,
  type WorkflowUndoRefusal,
  type WorkflowUndoResult,
} from "@amoreg/contracts";
import {
  applyContentPatch,
  diffWorkflowContent,
  makeContentPatch,
  mergeWorkflowContent,
  revertChanges,
  type FacetNote,
  type WorkflowContent,
} from "@repo/shared/workflowDiff";
import { graphForSave, graphsEqual } from "@repo/shared/workflowEdit";
import { validateWorkflowGraph } from "@repo/shared/workflowGraph";

export type Content = WorkflowContent<WorkflowGraph>;

/** How long a missing version may stay missing before the chart asks for it. */
export const GAP_WAIT_MS = 1_500;
/** How long a failed catch-up waits before asking again, unless the server says. */
export const CATCH_UP_RETRY_MS = 5_000;
/** How long a catch-up may go unanswered before it counts as failed. */
export const CATCH_UP_TIMEOUT_MS = 10_000;
export const UNDO_LIMIT = 100;
/** Typing in one panel field counts as one edit while it lasts. */
export const COALESCE_MS = 1_500;
/** How many of this chart's own `saveId`s it remembers, to know its saves when they come back. */
const OWN_SAVES_KEPT = 32;

const LIMITS = { nodes: WORKFLOW_LIMITS.nodes, edges: WORKFLOW_LIMITS.edges };
const UNBOUNDED = { nodes: Number.POSITIVE_INFINITY, edges: Number.POSITIVE_INFINITY };

/** What a save sends: the graph as it will run, and the name. */
export interface SaveBody {
  readonly graph: WorkflowGraph;
  readonly name: string;
}

interface Sent {
  readonly saveId: string;
  readonly expectedVersion: number;
  readonly body: SaveBody;
  /** The request as sent; a retry reuses `saveId` only when it would send exactly this. */
  readonly bytes: string;
}

type SaveSlot =
  /** `urgent`: save without the autosave delay (after a 409, or Retry). `last`: a failed send, for a byte-identical retry. */
  | { readonly kind: "idle"; readonly urgent: boolean; readonly last: Sent | null }
  | { readonly kind: "saving"; readonly sent: Sent }
  /** No automatic retry: a refused save would repeat forever. A new edit, or Retry, tries again. */
  | { readonly kind: "failed"; readonly sent: Sent; readonly message: string }
  /** Refused as out of date with no newer version to merge: waits for a newer confirmed version, or a new edit. */
  | { readonly kind: "behind"; readonly sent: Sent };

/** A version on its way in, from the room, a catch-up or this chart's own save reply. */
interface Arrival {
  readonly version: number;
  readonly change: WorkflowContentUpdate;
  readonly saveId: string | null;
  /** Null for a whole version, which names nobody. */
  readonly actor: WorkflowRevisionActor | null;
  readonly group: string | null;
  readonly reverts: string | null;
}

/** A version this chart applied, as it changed the confirmed content. */
export interface AppliedVersion {
  readonly version: number;
  readonly before: Content;
  readonly after: Content;
  /** One of this chart's own saves. */
  readonly mine: boolean;
  readonly actor: WorkflowRevisionActor | null;
  readonly group: string | null;
  /** The group it took back: an Undo or Redo. */
  readonly reverts: string | null;
  /** Some of it is not on the screen: the person's unsaved edits were kept there. */
  readonly withheld: boolean;
}

export type UndoDirection = "undo" | "redo";

interface Press {
  readonly direction: UndoDirection;
  readonly saveId: string;
  /** The request to take back, when the person named one (a summary's Undo); else whichever is newest. */
  readonly pinned: string | null;
  /** Pressed again, once, after the server said the request it named was already taken back. */
  readonly again: boolean;
}

/** The person's Undo and Redo of agent requests, which the server keeps. */
export interface RequestsState {
  /** As the server last said; null until first read. */
  readonly pointers: WorkflowRequestPointers | null;
  /** `again`: read again once the read in flight lands, since something changed after it was asked. */
  readonly read: "idle" | "reading" | "again";
  readonly sending: (Press & { readonly group: string }) | null;
  /** Presses that came while `sending` or a read was on its way, in order: each then sees the newest pointers. */
  readonly queued: ReadonlyArray<Press>;
  /** The group the server just refused to take back: the next press takes the person's own last edit before it. */
  readonly refused: string | null;
  /** The person edited this chart since they last undid a request here, which clears the server's Redo here as it clears theirs. */
  readonly edited: boolean;
}

interface LocalEdit {
  readonly before: Content;
  readonly after: Content;
  /** The confirmed version when it was made: versions after it came since. */
  readonly version: number;
}

export interface WorkflowDocState {
  /** The newest version this chart has applied, and its content. */
  readonly confirmed: { readonly version: number; readonly content: Content };
  /** What the person sees: the confirmed content plus their unsaved edits. */
  readonly local: Content;
  readonly save: SaveSlot;
  /** This chart's saves, newest last. */
  readonly mine: ReadonlyArray<Sent>;
  /** The room's id for this person (`sync.you`), to tell their other tabs' saves. */
  readonly you: string | null;
  /** The versions applied, oldest first, the newest {@link UNDO_LIMIT}. */
  readonly applied: ReadonlyArray<AppliedVersion>;
  /** Versions that came ahead of a missing one. */
  readonly early: ReadonlyMap<number, Arrival>;
  /** The newest version the chart has heard of. */
  readonly heard: number;
  readonly catchUp: {
    /** When the catch-up in flight was asked for. */
    readonly askedAt: number | null;
    /** When to ask for missing versions, while some are missing. */
    readonly dueAt: number | null;
    /** Catch-ups failed in a row: past one, a newer read is taken whole. */
    readonly failures: number;
  };
  /** The newest workflow read, kept for when a catch-up cannot be had. */
  readonly read: { readonly version: number; readonly content: Content } | null;
  readonly undo: ReadonlyArray<LocalEdit>;
  readonly redo: ReadonlyArray<LocalEdit>;
  readonly requests: RequestsState;
  /** The panel field being typed in, so its keystrokes count as one edit. */
  readonly typing: { readonly key: string; readonly at: number } | null;
}

/** Who saved a version a notice is about. */
export type Saver =
  | { readonly kind: "named"; readonly name: string }
  | { readonly kind: "other-tab" }
  | { readonly kind: "your-undo" }
  | { readonly kind: "your-redo" }
  | { readonly kind: "unknown" };

export type WorkflowDocNotice =
  /** Someone's changes to these steps were not applied: the person's unsaved edits there were kept. */
  | { readonly kind: "kept-yours"; readonly by: Saver; readonly steps: ReadonlyArray<string>; readonly workflow: boolean }
  /** A merge gave two steps one name that neither side had twice. */
  | { readonly kind: "merge-duplicate-name"; readonly by: Saver; readonly steps: ReadonlyArray<string> }
  /** A merge left these steps with nothing leading to them: `cut` is whose removed link did it. */
  | { readonly kind: "merge-unreachable"; readonly by: Saver; readonly cut: "theirs" | "yours"; readonly steps: ReadonlyArray<string> }
  /** Someone's changes would take the chart past the step or link limit with the person's: theirs were left out. */
  | { readonly kind: "merge-over-limit"; readonly by: Saver }
  | { readonly kind: "undo-refused"; readonly redo: boolean; readonly steps: ReadonlyArray<string>; readonly workflow: boolean }
  | { readonly kind: "undo-over-limit"; readonly redo: boolean }
  /** The server took back nothing of a request: `steps` are what refused, in the chart's order. */
  | { readonly kind: "request-refused"; readonly redo: boolean; readonly reason: WorkflowUndoRefusal; readonly steps: ReadonlyArray<WorkflowRefusedStep> }
  | { readonly kind: "request-failed"; readonly redo: boolean; readonly message: string }
  /** The request the person named (a summary's Undo) was taken back already, elsewhere. */
  | { readonly kind: "request-already-undone"; readonly group: string }
  /** The page was left with edits that could not be saved. */
  | { readonly kind: "left-unsaved"; readonly workflow: string };

export type WorkflowDocEffect =
  | { readonly type: "save"; readonly saveId: string; readonly expectedVersion: number; readonly body: SaveBody }
  | { readonly type: "catch-up"; readonly since: number }
  /** Read the workflow: the server has a newer version and named none. */
  | { readonly type: "read" }
  | { readonly type: "read-requests" }
  | { readonly type: "revert-request"; readonly direction: UndoDirection; readonly group: string; readonly saveId: string }
  | { readonly type: "notice"; readonly notice: WorkflowDocNotice };

export type WorkflowDocEvent =
  /** The person changed the chart: undoable, and typing under one `key` coalesces. */
  | { readonly type: "edited"; readonly update: (current: Content) => Content; readonly key: string | null; readonly at: number }
  /** A change that is not the chart's to undo: a rename in the header, the one-time relayout of an old chart. */
  | { readonly type: "adjusted"; readonly update: (current: Content) => Content }
  /** `saveId`: the key an Undo or Redo of a request is saved under, should it be one. `group`: that request and no other. */
  | { readonly type: "undo"; readonly saveId: string; readonly group?: string | undefined }
  | { readonly type: "redo"; readonly saveId: string }
  /** The person's pointers, or null when they could not be read. */
  | { readonly type: "requests-read"; readonly pointers: WorkflowRequestPointers | null; readonly at: number }
  | {
      readonly type: "request-answered";
      readonly saveId: string;
      readonly answer: WorkflowUndoResult | { readonly kind: "failed"; readonly message: string };
      readonly at: number;
    }
  /** Locked (AMO-305): unsaved edits can no longer be saved, so the screen goes back to the confirmed version. A save in flight lands first. */
  | { readonly type: "discarded" }
  /** The autosave delay passed, or Retry: save now if anything is unsaved, under `saveId` unless this is a retry. */
  | { readonly type: "save-due"; readonly saveId: string }
  | { readonly type: "retry" }
  | { readonly type: "save-succeeded"; readonly saveId: string; readonly version: number; readonly content: Content; readonly at: number }
  | {
      readonly type: "save-conflicted";
      readonly saveId: string;
      readonly latest: { readonly version: number; readonly content: Content } | null;
      readonly at: number;
    }
  | { readonly type: "save-failed"; readonly saveId: string; readonly message: string }
  | { readonly type: "synced"; readonly version: number; readonly you: string; readonly at: number }
  | { readonly type: "revision"; readonly revision: WorkflowRevision; readonly at: number }
  | { readonly type: "caught-up"; readonly revisions: ReadonlyArray<WorkflowRevision>; readonly at: number }
  | { readonly type: "head-taken"; readonly version: number; readonly content: Content; readonly at: number }
  | { readonly type: "catch-up-failed"; readonly retryAfterMs: number | null; readonly at: number }
  /** The workflow was read (the compliance stream, a fallback). */
  | { readonly type: "read"; readonly version: number; readonly content: Content; readonly at: number }
  | { readonly type: "tick"; readonly at: number };

export interface WorkflowDocStep {
  readonly state: WorkflowDocState;
  readonly effects: ReadonlyArray<WorkflowDocEffect>;
}

export function initialDoc(version: number, content: Content): WorkflowDocState {
  return {
    confirmed: { version, content },
    local: content,
    save: { kind: "idle", urgent: false, last: null },
    mine: [],
    you: null,
    applied: [],
    early: new Map(),
    heard: version,
    catchUp: { askedAt: null, dueAt: null, failures: 0 },
    read: null,
    undo: [],
    redo: [],
    requests: { pointers: null, read: "idle", sending: null, queued: [], refused: null, edited: false },
    typing: null,
  };
}

/** What a save would send now, or null when the screen holds nothing unsaved. */
export function unsavedBody(state: WorkflowDocState): SaveBody | null {
  const graph = graphForSave(state.local.graph);
  const typed = state.local.name.trim();
  const name = typed === "" ? state.confirmed.content.name : typed;
  if (name === state.confirmed.content.name && graphsEqual(graph, state.confirmed.content.graph)) return null;
  return { graph, name };
}

export type SaveStatus = "saved" | "unsaved" | "saving" | "error";

export function saveStatus(state: WorkflowDocState): SaveStatus {
  if (state.save.kind === "saving") return "saving";
  if (unsavedBody(state) === null) return "saved";
  return state.save.kind === "failed" ? "error" : "unsaved";
}

/** How long to wait before a `save-due`, or null when no save is wanted. */
export function saveDelay(state: WorkflowDocState, autosaveDelayMs: number): number | null {
  const body = unsavedBody(state);
  if (body === null) return null;
  switch (state.save.kind) {
    case "saving":
      return null;
    case "failed":
      // Only a new edit or Retry saves again: both turn the slot idle.
      return null;
    case "behind":
      return state.confirmed.version > state.save.sent.expectedVersion ? 0 : null;
    case "idle":
      return state.save.urgent ? 0 : autosaveDelayMs;
  }
}

/** When the reducer next wants a `tick` (to ask for a missing version, or give up on an ask), or null. */
export const nextTick = (state: WorkflowDocState): number | null =>
  state.catchUp.askedAt !== null ? state.catchUp.askedAt + CATCH_UP_TIMEOUT_MS : behind(state) ? state.catchUp.dueAt : null;

export function reduceWorkflowDoc(state: WorkflowDocState, event: WorkflowDocEvent): WorkflowDocStep {
  switch (event.type) {
    case "edited":
      return { state: edited(state, event.update, event.key, event.at), effects: [] };
    case "adjusted": {
      const local = event.update(state.local);
      return { state: local === state.local ? state : { ...state, local, typing: null, save: afterEdit(state.save) }, effects: [] };
    }
    case "undo":
    case "redo":
      return press(state, { direction: event.type, saveId: event.saveId, pinned: event.type === "undo" ? (event.group ?? null) : null, again: false });
    case "requests-read":
      return requestsRead(state, event.pointers);
    case "request-answered":
      return answered(state, event.saveId, event.answer, event.at);
    case "discarded":
      return state.save.kind === "saving"
        ? { state, effects: [] }
        : {
            state: { ...state, local: state.confirmed.content, save: { kind: "idle", urgent: false, last: null }, undo: [], redo: [], typing: null },
            effects: [],
          };
    case "save-due":
      return saveDue(state, event.saveId);
    case "retry":
      return state.save.kind === "failed"
        ? { state: { ...state, save: { kind: "idle", urgent: true, last: state.save.sent } }, effects: [] }
        : { state, effects: [] };
    case "save-succeeded": {
      const settled = settleSave(state, event.saveId, { kind: "idle", urgent: false, last: null });
      return receive(
        settled,
        [{ version: event.version, change: { kind: "content", content: event.content }, saveId: event.saveId, actor: null, group: null, reverts: null }],
        event.at,
      );
    }
    case "save-conflicted": {
      if (state.save.kind !== "saving" || state.save.sent.saveId !== event.saveId) return { state, effects: [] };
      // Nothing newer to merge: saving again would only meet another 409.
      if (event.latest === null || event.latest.version <= state.confirmed.version) {
        return { state: { ...state, save: { kind: "behind", sent: state.save.sent } }, effects: [{ type: "read" }] };
      }
      return takeHead({ ...state, save: { kind: "idle", urgent: true, last: null } }, event.latest, event.at);
    }
    case "save-failed":
      return state.save.kind === "saving" && state.save.sent.saveId === event.saveId
        ? { state: { ...state, save: { kind: "failed", sent: state.save.sent, message: event.message } }, effects: [] }
        : { state, effects: [] };
    case "synced": {
      const asked = askRequests({ ...state, you: event.you, heard: Math.max(state.heard, event.version) });
      return applyInOrder(asked.state, asked.effects, event.at, event.version > state.confirmed.version);
    }
    case "revision":
      return receive(state, [arrivalOf(event.revision)], event.at);
    case "caught-up":
      return receive(
        { ...state, catchUp: { askedAt: null, dueAt: null, failures: 0 } },
        event.revisions.map(arrivalOf),
        event.at,
      );
    case "head-taken":
      return takeHead({ ...state, catchUp: { askedAt: null, dueAt: null, failures: 0 } }, event, event.at);
    case "catch-up-failed":
      return catchUpFailed(state, event.retryAfterMs, event.at);
    case "read": {
      if (event.version <= state.confirmed.version) {
        // The read a 409 asked for has nothing newer: only the person can say to save again.
        return state.save.kind === "behind"
          ? { state: { ...state, save: { kind: "failed", sent: state.save.sent, message: NOTHING_NEWER } }, effects: [] }
          : { state, effects: [] };
      }
      const read = { ...state, read: { version: event.version, content: event.content }, heard: Math.max(state.heard, event.version) };
      // A catch-up has failed, or a save asked for this read: it is the best there is. Otherwise ask, for the cues the read would not give.
      return state.catchUp.failures > 0 || state.save.kind === "behind" ? takeHead(read, read.read, event.at) : applyInOrder(read, [], event.at, true);
    }
    case "tick":
      return state.catchUp.askedAt !== null && event.at >= state.catchUp.askedAt + CATCH_UP_TIMEOUT_MS
        ? catchUpFailed(state, null, event.at)
        : applyInOrder(state, [], event.at, false);
  }
}

const NOTHING_NEWER = "This workflow changed elsewhere and the newer version could not be loaded.";

/** A catch-up that failed: take the newest read if it is newer, else ask for one. */
function catchUpFailed(state: WorkflowDocState, retryAfterMs: number | null, at: number): WorkflowDocStep {
  const failed = {
    ...state,
    catchUp: { askedAt: null, dueAt: at + (retryAfterMs ?? CATCH_UP_RETRY_MS), failures: state.catchUp.failures + 1 },
  };
  if (failed.read && failed.read.version > failed.confirmed.version) return takeHead(failed, failed.read, at);
  return applyInOrder(failed, behind(failed) ? [{ type: "read" }] : [], at, false);
}

const arrivalOf = (revision: WorkflowRevision): Arrival => ({
  version: revision.version,
  change: revision.change,
  saveId: revision.saveId,
  actor: revision.actor,
  group: revision.group,
  reverts: revision.reverts,
});

/** The person changed the screen: a failed save may go again, after the usual delay. */
const afterEdit = (save: SaveSlot): SaveSlot =>
  save.kind === "failed"
    ? { kind: "idle", urgent: false, last: save.sent }
    : save.kind === "behind" || (save.kind === "idle" && save.urgent)
      ? { kind: "idle", urgent: false, last: save.kind === "idle" ? save.last : null }
      : save;

const isMine = (state: WorkflowDocState, saveId: string | null) => saveId !== null && state.mine.some((sent) => sent.saveId === saveId);

export const isRedo = (version: { readonly group: string | null; readonly reverts: string | null }) => version.reverts !== null && version.group?.startsWith("revert:redo:") === true;

function saverOf(state: WorkflowDocState, arrival: Arrival): Saver {
  const { actor } = arrival;
  if (actor === null || actor.via === "backfill") return { kind: "unknown" };
  if (actor.via === "person" && state.you !== null && actor.person === state.you) {
    return arrival.reverts === null ? { kind: "other-tab" } : { kind: isRedo(arrival) ? "your-redo" : "your-undo" };
  }
  return { kind: "named", name: actor.name };
}

const withApplied = (state: WorkflowDocState, applied: AppliedVersion): WorkflowDocState => ({
  ...state,
  applied: [...state.applied, applied].slice(-UNDO_LIMIT),
});

const bytesOf = (expectedVersion: number, body: SaveBody) => JSON.stringify({ expectedVersion, ...body });

const behind = (state: WorkflowDocState) => state.heard > state.confirmed.version;

function edited(state: WorkflowDocState, update: (current: Content) => Content, key: string | null, at: number): WorkflowDocState {
  const local = update(state.local);
  if (local === state.local) return state;
  const top = state.undo.at(-1);
  const coalesce = top !== undefined && key !== null && state.typing?.key === key && at - state.typing.at <= COALESCE_MS;
  const undo = coalesce
    ? [...state.undo.slice(0, -1), { ...top, after: local }]
    : [...state.undo, { before: state.local, after: local, version: state.confirmed.version }];
  return {
    ...state,
    local,
    save: afterEdit(state.save),
    undo: undo.length > UNDO_LIMIT ? undo.slice(-UNDO_LIMIT) : undo,
    redo: [],
    requests: { ...state.requests, edited: true },
    typing: key === null ? null : { key, at },
  };
}

const withRequests = (state: WorkflowDocState, requests: Partial<RequestsState>): WorkflowDocState => ({
  ...state,
  requests: { ...state.requests, ...requests },
});

function askRequests(state: WorkflowDocState): WorkflowDocStep {
  if (state.requests.read !== "idle") return { state: state.requests.read === "again" ? state : withRequests(state, { read: "again" }), effects: [] };
  return { state: withRequests(state, { read: "reading" }), effects: [{ type: "read-requests" }] };
}

function requestTarget(state: WorkflowDocState, direction: UndoDirection): string | null {
  const pointer = state.requests.pointers?.[direction] ?? null;
  if (pointer === null || (direction === "redo" && state.requests.edited)) return null;
  const local = state[direction].at(-1);
  if (local === undefined) return pointer.group;
  return pointer.group !== state.requests.refused && pointer.version > local.version ? pointer.group : null;
}

const waiting = (requests: RequestsState) => requests.sending !== null || requests.read !== "idle";

function press(state: WorkflowDocState, pressed: Press): WorkflowDocStep {
  const { requests } = state;
  if (waiting(requests)) return { state: withRequests(state, { queued: [...requests.queued, pressed] }), effects: [] };
  const group = pressed.pinned ?? requestTarget(state, pressed.direction);
  if (group === null) return takeBack(requests.refused === null ? state : withRequests(state, { refused: null }), pressed.direction);
  const sending = { ...pressed, group };
  return {
    state: { ...withRequests(state, { sending, refused: null, ...(pressed.direction === "undo" ? { edited: false } : {}) }), typing: null },
    effects: [{ type: "revert-request", direction: pressed.direction, group, saveId: pressed.saveId }],
  };
}

function pressQueued(start: WorkflowDocStep): WorkflowDocStep {
  let step = start;
  while (step.state.requests.queued.length > 0 && !waiting(step.state.requests)) {
    const [next, ...rest] = step.state.requests.queued as [Press, ...Array<Press>];
    const pressed = press(withRequests(step.state, { queued: rest }), next);
    step = { state: pressed.state, effects: [...step.effects, ...pressed.effects] };
  }
  return step;
}

function requestsRead(state: WorkflowDocState, pointers: WorkflowRequestPointers | null): WorkflowDocStep {
  const again = state.requests.read === "again";
  const read = withRequests(state, { pointers: pointers ?? state.requests.pointers, read: again ? "reading" : "idle" });
  return pressQueued({ state: read, effects: again ? [{ type: "read-requests" }] : [] });
}

function answered(
  state: WorkflowDocState,
  saveId: string,
  answer: Extract<WorkflowDocEvent, { readonly type: "request-answered" }>["answer"],
  at: number,
): WorkflowDocStep {
  const { sending } = state.requests;
  if (sending === null || sending.saveId !== saveId) return { state, effects: [] };
  const redo = sending.direction === "redo";
  const settled = withRequests(state, {
    sending: null,
    refused: answer.kind === "refused" ? sending.group : null,
    // A read on its way may have been asked before this answer's change.
    read: state.requests.read === "idle" ? "idle" : "again",
    ...(answer.kind === "failed" ? {} : { pointers: answer.pointers }),
  });
  switch (answer.kind) {
    case "reverted": {
      const heard = { ...settled, heard: Math.max(settled.heard, answer.version) };
      return pressQueued(applyInOrder(heard, [], at, false));
    }
    case "refused":
      return pressQueued({
        state: settled,
        effects: [{ type: "notice", notice: { kind: "request-refused", redo, reason: answer.reason, steps: answer.steps } }],
      });
    case "already-undone":
      // A named request is the one the person meant: nothing else to take back in its place.
      if (sending.pinned !== null) {
        return pressQueued({ state: settled, effects: [{ type: "notice", notice: { kind: "request-already-undone", group: sending.pinned } }] });
      }
      return pressQueued({
        state: sending.again ? settled : withRequests(settled, { queued: [{ ...sending, again: true }, ...settled.requests.queued] }),
        effects: [],
      });
    case "failed": {
      const asked = askRequests(settled);
      return pressQueued({
        state: asked.state,
        effects: [{ type: "notice", notice: { kind: "request-failed", redo, message: answer.message } }, ...asked.effects],
      });
    }
  }
}

function takeBack(state: WorkflowDocState, direction: "undo" | "redo"): WorkflowDocStep {
  const from = direction === "undo" ? state.undo : state.redo;
  const entry = from.at(-1);
  if (!entry) return { state, effects: [] };
  const rest = from.slice(0, -1);
  // Someone else's changes the merge kept off the screen since: carried onto the edit's result, so the undo sees them.
  const withheld = state.applied
    .filter((applied) => applied.version > entry.version && !applied.mine && applied.withheld)
    .map((applied) => ({ before: entry.after, after: carried(applied, entry.after), undoing: false }));
  const result = revertChanges({
    changes: [{ before: entry.before, after: entry.after, undoing: true }, ...withheld],
    head: state.local,
    limits: LIMITS,
  });
  const redo = direction === "redo";
  if (result.kind !== "reverted") {
    // Nothing was taken back, and the entry is dropped: it would refuse again, and stand in front of older ones.
    const notice: WorkflowDocNotice =
      result.kind === "refused" ? { kind: "undo-refused", redo, ...namesOf(result.refused) } : { kind: "undo-over-limit", redo };
    return { state: { ...state, [direction]: rest, typing: null }, effects: [{ type: "notice", notice }] };
  }
  const moved = { before: state.local, after: result.content, version: state.confirmed.version };
  return {
    state: {
      ...state,
      local: result.content,
      save: afterEdit(state.save),
      undo: redo ? [...state.undo, moved] : rest,
      redo: redo ? rest : [...state.redo, moved],
      typing: null,
    },
    effects: [],
  };
}

/**
 * `content` with `applied`'s changes on it, theirs winning: what applying all
 * of it there would touch. Where the merge keeps their side whole (to keep a
 * step reachable), their patch goes on instead, step by step, so the
 * person's own edits elsewhere are not counted as theirs.
 */
function carried(applied: AppliedVersion, content: Content): Content {
  const merged = mergeWorkflowContent({ base: applied.before, ours: applied.after, theirs: content, limits: UNBOUNDED });
  if (merged.kind === "merged" && !diffWorkflowContent(merged.content, applied.after).empty) return merged.content;
  return applyContentPatch(content, makeContentPatch(applied.before, applied.after));
}

function saveDue(state: WorkflowDocState, freshId: string): WorkflowDocStep {
  if (state.save.kind === "saving") return { state, effects: [] };
  if (state.save.kind === "behind" && state.confirmed.version <= state.save.sent.expectedVersion) return { state, effects: [] };
  const body = unsavedBody(state);
  if (body === null) return { state: state.save.kind === "idle" && !state.save.urgent ? state : { ...state, save: { kind: "idle", urgent: false, last: null } }, effects: [] };
  const expectedVersion = state.confirmed.version;
  const bytes = bytesOf(expectedVersion, body);
  const previous = state.save.kind === "failed" ? state.save.sent : state.save.kind === "idle" ? state.save.last : null;
  const saveId = previous?.bytes === bytes ? previous.saveId : freshId;
  const sent: Sent = { saveId, expectedVersion, body, bytes };
  const mine = [...state.mine.filter((each) => each.saveId !== saveId), sent].slice(-OWN_SAVES_KEPT);
  return { state: { ...state, save: { kind: "saving", sent }, mine }, effects: [{ type: "save", saveId, expectedVersion, body }] };
}

/** The reply to the save in flight frees the slot; a reply to any other save changes nothing here. */
function settleSave(state: WorkflowDocState, saveId: string, next: SaveSlot): WorkflowDocState {
  return state.save.kind === "saving" && state.save.sent.saveId === saveId ? { ...state, save: next } : state;
}

/** Queue versions, apply every one that is next in order, and ask for missing ones when due. */
function receive(state: WorkflowDocState, arrivals: ReadonlyArray<Arrival>, at: number): WorkflowDocStep {
  let early = state.early;
  let heard = state.heard;
  for (const arrival of arrivals) {
    heard = Math.max(heard, arrival.version);
    if (arrival.version <= state.confirmed.version || early.has(arrival.version)) continue;
    if (early === state.early) early = new Map(early);
    (early as Map<number, Arrival>).set(arrival.version, arrival);
  }
  return applyInOrder({ ...state, early, heard }, [], at, false);
}

/**
 * Apply every queued version that is next, then decide about the rest: when
 * nothing is missing the gap closes; when something is, ask for it once it is
 * due (`urgent`: now, else {@link GAP_WAIT_MS} after it went missing).
 */
function applyInOrder(start: WorkflowDocState, effects: ReadonlyArray<WorkflowDocEffect>, at: number, urgent: boolean): WorkflowDocStep {
  let state = start;
  const out = [...effects];
  for (let next = state.early.get(state.confirmed.version + 1); next; next = state.early.get(state.confirmed.version + 1)) {
    const applied = apply(state, next);
    state = applied.state;
    out.push(...applied.effects);
  }
  if (state.early.size > 0 && [...state.early.keys()].some((version) => version <= state.confirmed.version)) {
    state = { ...state, early: new Map([...state.early].filter(([version]) => version > state.confirmed.version)) };
  }
  if (!behind(state)) {
    return { state: state.catchUp.dueAt === null ? state : { ...state, catchUp: { ...state.catchUp, dueAt: null } }, effects: out };
  }
  const dueAt = urgent ? Math.min(at, state.catchUp.dueAt ?? at) : (state.catchUp.dueAt ?? at + GAP_WAIT_MS);
  if (state.catchUp.askedAt === null && dueAt <= at) {
    return {
      state: { ...state, catchUp: { ...state.catchUp, askedAt: at, dueAt: null } },
      effects: [...out, { type: "catch-up", since: state.confirmed.version }],
    };
  }
  return { state: dueAt === state.catchUp.dueAt ? state : { ...state, catchUp: { ...state.catchUp, dueAt } }, effects: out };
}

/** The next version, applied: this chart's own moves the confirmed version only; anyone else's merges in. */
function apply(state: WorkflowDocState, arrival: Arrival): WorkflowDocStep {
  const early = new Map(state.early);
  early.delete(arrival.version);
  const theirs = applyContentPatch(state.confirmed.content, arrival.change);
  const confirmed = { version: arrival.version, content: theirs };
  const applied = {
    version: arrival.version,
    before: state.confirmed.content,
    after: theirs,
    actor: arrival.actor,
    group: arrival.group,
    reverts: arrival.reverts,
  };
  if (isMine(state, arrival.saveId)) {
    // A failed save that did land (its reply was lost) is done.
    const save =
      (state.save.kind === "failed" || state.save.kind === "behind") && state.save.sent.saveId === arrival.saveId
        ? { kind: "idle" as const, urgent: false, last: null }
        : state.save;
    return { state: withApplied({ ...state, early, confirmed, save }, { ...applied, mine: true, withheld: false }), effects: [] };
  }
  const merged = mergeIn({ ...state, early }, confirmed, saverOf(state, arrival));
  const next = withApplied(merged.state, { ...applied, mine: false, withheld: merged.withheld });
  if (!movesPointers(state, arrival)) return { state: next, effects: merged.effects };
  const asked = askRequests(next);
  return { state: asked.state, effects: [...merged.effects, ...asked.effects] };
}

const movesPointers = (state: WorkflowDocState, arrival: Arrival) =>
  arrival.actor !== null &&
  arrival.actor.via !== "backfill" &&
  state.you !== null &&
  arrival.actor.person === state.you &&
  (arrival.actor.via === "agent" || arrival.reverts !== null);

/**
 * A version taken whole (a catch-up's head, a 409's latest, a read), applied
 * like any other, with versions it covers dropped. It is this chart's own
 * when it is what one of its saves over the confirmed version would make.
 */
function takeHead(state: WorkflowDocState, head: { readonly version: number; readonly content: Content }, at: number): WorkflowDocStep {
  const heard = Math.max(state.heard, head.version);
  if (head.version <= state.confirmed.version) return applyInOrder({ ...state, heard }, [], at, false);
  // A save sends no description: one that changed is someone else's.
  const own =
    head.version === state.confirmed.version + 1 && head.content.description === state.confirmed.content.description
      ? state.mine.find((sent) => sent.expectedVersion === state.confirmed.version && savedAs(sent.body, head.content))
      : undefined;
  const applied = apply(
    { ...state, heard },
    { version: head.version, change: { kind: "content", content: head.content }, saveId: own?.saveId ?? null, actor: null, group: null, reverts: null },
  );
  return applyInOrder(applied.state, applied.effects, at, false);
}

const savedAs = (body: SaveBody, content: Content) => body.name === content.name && graphsEqual(body.graph, graphForSave(content.graph));

/** `confirmed` merged into the screen; `withheld` when some of it was kept off it. */
function mergeIn(
  state: WorkflowDocState,
  confirmed: WorkflowDocState["confirmed"],
  by: Saver,
): WorkflowDocStep & { readonly withheld: boolean } {
  const base = { ...state, confirmed, typing: null };
  // Nothing unsaved here: theirs is the screen.
  if (sameContent(state.local, state.confirmed.content)) return { state: { ...base, local: confirmed.content }, effects: [], withheld: false };
  const ours = asSaved(state.local, state.confirmed.content);
  const merged = mergeWorkflowContent({ base: state.confirmed.content, ours: ours.content, theirs: confirmed.content, limits: LIMITS });
  if (merged.kind === "over-limit") {
    return { state: base, effects: [{ type: "notice", notice: { kind: "merge-over-limit", by } }], withheld: true };
  }
  const restored = ours.restore(merged.content, state.confirmed.content);
  const local = sameContent(restored, confirmed.content) ? confirmed.content : restored;
  const effects: WorkflowDocEffect[] = [];
  if (merged.conflicts.length > 0) effects.push({ type: "notice", notice: { kind: "kept-yours", by, ...namesOf(merged.conflicts) } });
  for (const notice of problemsMade({ ours: state.local.graph, theirs: confirmed.content.graph, merged: local.graph }, by)) {
    effects.push({ type: "notice", notice });
  }
  return { state: { ...base, local }, effects, withheld: merged.conflicts.length > 0 };
}

/**
 * The screen as it saves: a label or name that differs from the confirmed
 * one only by what saving does to it (trimmed; empty takes the default) is
 * not an edit, so it cannot win over someone else's change to it. `restore`
 * puts the text as typed back wherever the merge kept the confirmed value.
 */
function asSaved(local: Content, saved: Content) {
  const normal = graphForSave(local.graph);
  const savedLabels = new Map(saved.graph.nodes.map((node) => [node.id, node.label]));
  const typed = new Map<string, string>();
  const nodes = local.graph.nodes.map((node, at) => {
    const label = normal.nodes[at]?.label ?? node.label;
    if (label === node.label || savedLabels.get(node.id) !== label) return node;
    typed.set(node.id, node.label);
    return { ...node, label };
  });
  const typedName = local.name !== saved.name && (local.name.trim() || saved.name) === saved.name ? local.name : null;
  const content: Content = {
    ...local,
    ...(typedName === null ? {} : { name: saved.name }),
    graph: typed.size === 0 ? local.graph : { ...local.graph, nodes },
  };
  return {
    content: typed.size === 0 && typedName === null ? local : content,
    restore: (merged: Content, base: Content): Content => {
      if (typed.size === 0 && typedName === null) return merged;
      const baseLabels = new Map(base.graph.nodes.map((node) => [node.id, node.label]));
      const keep = (node: WorkflowGraph["nodes"][number]) => {
        const raw = typed.get(node.id);
        return raw !== undefined && node.label === baseLabels.get(node.id) ? { ...node, label: raw } : node;
      };
      return {
        ...merged,
        ...(typedName !== null && merged.name === base.name ? { name: typedName } : {}),
        graph: typed.size === 0 ? merged.graph : { ...merged.graph, nodes: merged.graph.nodes.map(keep) },
      };
    },
  };
}

const sameContent = (left: Content, right: Content) =>
  left.name === right.name && left.description === right.description && graphsEqual(left.graph, right.graph);

/**
 * What the merge did that neither side asked for. A merge never makes a loop
 * (W2 keeps ours whole instead), but it can repeat a step name neither side
 * repeated, and leave a step with nothing leading to it that the person's
 * side reached: one inside a branch theirs cut, or one only theirs has under
 * a link ours removed. Those steps are named by where the cut is, the first
 * of them along the chart.
 */
function problemsMade(
  graphs: { readonly ours: WorkflowGraph; readonly theirs: WorkflowGraph; readonly merged: WorkflowGraph },
  by: Saver,
): ReadonlyArray<WorkflowDocNotice> {
  const issuesOf = (graph: WorkflowGraph, code: string) =>
    new Set(validateWorkflowGraph(graphForSave(graph)).flatMap((issue) => (issue.code === code && issue.nodeId ? [issue.nodeId] : [])));
  const merged = graphForSave(graphs.merged);
  const labelOf = (id: string) => merged.nodes.find((node) => node.id === id)?.label ?? "";
  const notices: WorkflowDocNotice[] = [];

  const repeatedIn = (graph: WorkflowGraph) => {
    const ids = issuesOf(graph, "duplicate-name");
    return graphForSave(graph).nodes.flatMap((node) => (ids.has(node.id) ? [node.label] : []));
  };
  const repeated = new Set([...repeatedIn(graphs.ours), ...repeatedIn(graphs.theirs)]);
  const named = [...new Set(repeatedIn(merged))].filter((name) => !repeated.has(name));
  if (named.length > 0) notices.push({ kind: "merge-duplicate-name", by, steps: named });

  const oursHas = new Set(graphs.ours.nodes.map((node) => node.id));
  const oursStranded = issuesOf(graphs.ours, "unreachable");
  const theirsStranded = issuesOf(graphs.theirs, "unreachable");
  // Theirs cut it: ours reaches the step and theirs does not. Ours cut it: only theirs has it, and theirs reaches it.
  const cutBy = (id: string) => (oursHas.has(id) ? (oursStranded.has(id) ? null : "theirs") : theirsStranded.has(id) ? null : "yours");
  const stranded = [...issuesOf(merged, "unreachable")];
  for (const cut of ["theirs", "yours"] as const) {
    const ids = new Set(stranded.filter((id) => cutBy(id) === cut));
    const first = [...ids].filter((id) => !merged.edges.some((edge) => edge.target === id && ids.has(edge.source)));
    const steps = (first.length > 0 ? first : [...ids]).map(labelOf);
    if (steps.length > 0) notices.push({ kind: "merge-unreachable", by, cut, steps });
  }
  return notices;
}

/** The steps a list of facet notes names, by label, in order, and whether the workflow's own name or description is among them. */
function namesOf(notes: ReadonlyArray<FacetNote>): { readonly steps: ReadonlyArray<string>; readonly workflow: boolean } {
  const steps = [...new Set(notes.flatMap((note) => (note.stepLabel === null ? [] : [note.stepLabel])))];
  return { steps, workflow: notes.some((note) => note.stepId === null) };
}

const list = (names: ReadonlyArray<string>): string => {
  const quoted = names.map((name) => `“${name}”`);
  if (quoted.length <= 1) return quoted[0] ?? "";
  const shown = quoted.length > 3 ? [...quoted.slice(0, 2), `${quoted.length - 2} more`] : quoted;
  return `${shown.slice(0, -1).join(", ")} and ${shown.at(-1)}`;
};

const subject = (steps: ReadonlyArray<string>, workflow: boolean) =>
  steps.length > 0 ? list(steps) : workflow ? "the workflow's name" : "the chart";

const who = (by: Saver, start: boolean): string => {
  switch (by.kind) {
    case "named":
      return by.name;
    case "other-tab":
      return start ? "Your other tab" : "your other tab";
    case "your-undo":
      return start ? "Your undo" : "your undo";
    case "your-redo":
      return start ? "Your redo" : "your redo";
    case "unknown":
      return start ? "A colleague" : "a colleague";
  }
};

/** The sentence a notice is shown as. */
export function noticeText(notice: WorkflowDocNotice): string {
  switch (notice.kind) {
    case "kept-yours":
      return `Kept your edits to ${subject(notice.steps, notice.workflow)}. ${who(notice.by, true)}'s changes there were not applied.`;
    case "merge-duplicate-name":
      return `Your edits and ${who(notice.by, false)}'s gave two steps the name ${list(notice.steps)}.`;
    case "merge-unreachable": {
      const them = notice.steps.length > 1 ? "them" : "it";
      return notice.cut === "theirs"
        ? `${who(notice.by, true)} removed the link into ${list(notice.steps)}, so nothing leads to ${them} now.`
        : `${who(notice.by, true)} added ${list(notice.steps)} below a link you removed, so nothing leads to ${them} now.`;
    }
    case "merge-over-limit":
      return `${who(notice.by, true)}'s changes would take this chart past ${WORKFLOW_LIMITS.nodes} steps with yours, so yours were kept and theirs left out.`;
    case "undo-refused":
      return `Could not ${notice.redo ? "redo" : "undo"}: ${subject(notice.steps, notice.workflow)} changed since.`;
    case "undo-over-limit":
      return `${notice.redo ? "Redoing" : "Undoing"} that would take this chart past ${WORKFLOW_LIMITS.nodes} steps.`;
    case "request-refused":
      return requestRefusedText(notice);
    case "request-failed":
      return `Could not ${notice.redo ? "redo" : "undo"}: ${notice.message}`;
    case "request-already-undone":
      return "eve's changes were already undone.";
    case "left-unsaved":
      return `Your last edits to “${notice.workflow}” could not be saved.`;
  }
}

function requestRefusedText(notice: Extract<WorkflowDocNotice, { readonly kind: "request-refused" }>): string {
  const verb = notice.redo ? "redo" : "undo";
  const [first] = notice.steps;
  const name = (label: string | null) => (label === null ? "a removed step" : `“${label}”`);
  const more = new Set(notice.steps.map((step) => step.stepId)).size - 1;
  const others = more > 0 ? ` and ${more} more` : "";
  switch (notice.reason) {
    case "changed-since": {
      if (first === undefined) return `Could not ${verb}: this workflow kept changing. Try again.`;
      const what =
        first.stepId === null
          ? `the workflow's ${first.field === "description" ? "description" : "name"}`
          : first.target
            ? `the link from ${name(first.stepLabel)} to ${name(first.target.stepLabel)}`
            : name(first.stepLabel);
      return `Could not ${verb}: ${what}${others} changed since.`;
    }
    case "new-problem":
      return `Could not ${verb}: it would put ${name(first?.stepLabel ?? null)}${others} on a loop or give it another step's name.`;
    case "over-limit":
      return `${notice.redo ? "Redoing" : "Undoing"} that would take this chart past ${WORKFLOW_LIMITS.nodes} steps.`;
    case "no-earlier-version":
      return `Could not ${verb}: there is no earlier version of this workflow to go back to.`;
  }
}
